import { cn } from '@/lib/utils';
import type { MessageReaction, ReactionUser } from '../types';
import { useUserAvatar } from '../hooks';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CustomEmoji } from './CustomEmoji';

interface ReactionsProps {
  reactions: MessageReaction[];
  isOutgoing?: boolean;
  className?: string;
}

/**
 * Аватар пользователя в реакции
 */
function ReactionUserAvatar({ user }: { user: ReactionUser }) {
  const { url } = useUserAvatar(user.id);
  const initials = user.visibleName.slice(0, 1).toUpperCase();

  return (
    <Avatar className="h-5 w-5 border border-background">
      {url && <AvatarImage src={url} alt={user.visibleName} />}
      <AvatarFallback className="text-[8px] font-medium bg-muted">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Компонент для отображения реакций под сообщением
 * Показывает аватарку пользователя и эмодзи рядом (для личных чатов)
 */
export function Reactions({ reactions, isOutgoing, className }: ReactionsProps) {
  if (!reactions || reactions.length === 0) {return null;}

  return (
    <div className={cn('flex flex-wrap gap-1 mt-1', className)}>
      {reactions.map((reaction, index) => {
        // Если есть информация о пользователях — показываем каждого отдельно
        if (reaction.users && reaction.users.length > 0) {
          return reaction.users.map((user) => (
            <div
              key={`${reaction.emoji}-${user.id}`}
              className={cn(
                'inline-flex items-center gap-1 pl-0.5 pr-2 py-0.5 rounded-full text-xs transition-colors',
                reaction.isChosen
                  ? (isOutgoing
                    ? 'bg-white/30 text-white'
                    : 'bg-primary/20 text-primary')
                  : (isOutgoing
                    ? 'bg-white/15 text-white/90'
                    : 'bg-muted text-foreground'),
                'cursor-pointer hover:scale-105 transition-transform'
              )}
              title={`${user.visibleName}: ${reaction.emoji}`}
            >
              {/* Аватарка пользователя */}
              <ReactionUserAvatar user={user} />

              {/* Emoji */}
              <span className="text-sm leading-none">
                {reaction.customEmojiId ? (
                  <CustomEmoji customEmojiId={reaction.customEmojiId} className="w-4 h-4" />
                ) : (
                  reaction.emoji
                )}
              </span>
            </div>
          ));
        }

        // Fallback: если нет информации о пользователях — показываем как раньше (эмодзи + счетчик)
        return (
          <div
            key={`${reaction.emoji}-${index}`}
            className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors',
              reaction.isChosen
                ? (isOutgoing
                  ? 'bg-white/30 text-white'
                  : 'bg-primary/20 text-primary')
                : (isOutgoing
                  ? 'bg-white/15 text-white/90'
                  : 'bg-muted text-foreground'),
              'cursor-pointer hover:scale-105 transition-transform'
            )}
            title={reaction.customEmojiId ? 'Пользовательский эмодзи' : reaction.emoji}
          >
            {/* Emoji or custom emoji */}
            <span className="text-sm leading-none">
              {reaction.customEmojiId ? (
                <CustomEmoji customEmojiId={reaction.customEmojiId} className="w-4 h-4" />
              ) : (
                reaction.emoji
              )}
            </span>

            {/* Count */}
            {reaction.count > 1 && (
              <span className="font-medium">{reaction.count}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
